import { CircularProgress } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import LabelVideoStore from "store/labelVideoStore";
import { inject, observer } from "mobx-react";
import { theme } from "./theme";
import { formatTime } from "../utils/formatTime";
import IncidentIntervalItem from "./IncidentIntervalItem";
import IncidentPredictionIntervalItem from "./IncidentPredictionIntervalItem";

export const INCIDENT_ROW_GAP = 8;
export const INCIDENT_ROW_HEIGHT = 36;
export const INCIDENT_ROW_HEIGHT_WITH_PREDICTIONS = 66;
export const INCIDENT_LIST_MARGIN_TOP = 15;

export const getIncidentRowHeight = (hasPredictions: boolean) =>
  hasPredictions ? INCIDENT_ROW_HEIGHT_WITH_PREDICTIONS : INCIDENT_ROW_HEIGHT;

type Props = {
  labelVideoStore?: LabelVideoStore;
  clientWidth: number;
  onFrameChange: (frameIndex: number) => void;
  handleDragBar: (e: React.MouseEvent) => void;
  showLabels?: boolean;
};

const IncidentEditor = ({
  labelVideoStore,
  clientWidth,
  onFrameChange,
  handleDragBar,
  showLabels = false,
}: Props) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartFrame, setDragStartFrame] = useState(0);
  const [isDraggingLeft, setIsDraggingLeft] = useState(false);
  const [hoverInfo, setHoverInfo] = React.useState<{
    time: string;
    frame: number;
  } | null>(null);
  const [mousePosition, setMousePosition] = React.useState<{
    x: number;
    y: number;
  } | null>(null);

  const classList = labelVideoStore?.classList ?? [];
  const readOnly = labelVideoStore?.isReadOnly ?? false;
  const hasPredictions = (labelVideoStore?.predictionIncidents?.length ?? 0) > 0;
  const rowHeight = getIncidentRowHeight(hasPredictions);

  const handleMouseDown = (
    e: React.MouseEvent,
    incident: any,
    isLeft: boolean
  ) => {
    if (readOnly) return;
    e.stopPropagation();
    if (isLeft && incident.endFrame == null) {
      incident.endFrame = labelVideoStore?.currentFrame ?? 0;
      incident.isFinished = true;
    }
    const newFrame = isLeft
        ? incident.startFrame
        : incident.endFrame ?? labelVideoStore?.currentFrame ?? 0
    setIsDragging(true);
    setIsDraggingLeft(isLeft);
    setDragStartX(e.clientX);
    setDragStartFrame(newFrame);
    document.body.style.cursor = "grabbing";
  };

  const handleDragMove = (e: MouseEvent) => {
    if (!isDragging || !labelVideoStore) return;

    const deltaX = e.clientX - dragStartX;
    const frameDelta = Math.round(
      (deltaX * labelVideoStore.totalFrames) / clientWidth
    );
    const newFrame = Math.max(
      0,
      Math.min(labelVideoStore.totalFrames, dragStartFrame + frameDelta)
    );

    const currentIncident = labelVideoStore.incidents.find(
      (inc) => inc.id === labelVideoStore?.clickedIncident
    );
    if (currentIncident) {
      if (isDraggingLeft) {
        if (
          newFrame < (currentIncident.endFrame ?? labelVideoStore.currentFrame)
        ) {
          currentIncident.startFrame = newFrame;
          onFrameChange(newFrame);
        }
      } else {
        if (newFrame > currentIncident.startFrame) {
          currentIncident.endFrame = newFrame;
          onFrameChange(newFrame);
        }
      }
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    document.body.style.cursor = "default";
  };

  const handleAdjustFrame = (
    incident: any,
    isLeft: boolean,
    increment: boolean
  ) => {
    if (readOnly) return;
    const newFrame =
      (isLeft ? incident.startFrame : incident.endFrame ?? 0) +
      (increment ? 1 : -1);
    const currentIncident = labelVideoStore?.incidents.find(
      (inc) => inc.id === labelVideoStore?.clickedIncident
    );
    if (currentIncident) {
      if (isLeft) {
        if (currentIncident.endFrame == null) {
          currentIncident.endFrame = labelVideoStore?.currentFrame ?? 0;
          currentIncident.isFinished = true;
        }
        if (newFrame < currentIncident.endFrame) {
          currentIncident.startFrame = newFrame;
          onFrameChange(newFrame);
        }
      } else {
        if (newFrame > currentIncident.startFrame) {
          currentIncident.endFrame = newFrame;
          onFrameChange(newFrame);
        }
      }
    }
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleDragMove);
      document.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", handleDragMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragStartX, dragStartFrame]);

  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const frameIndex = Math.floor(
      (x / rect.width) * (labelVideoStore?.totalFrames || 0)
    );

    setHoverInfo({
      time: formatTime(frameIndex, labelVideoStore?.fps || 0),
      frame: frameIndex,
    });
    setMousePosition({ x: e.clientX, y: e.clientY });
  };

  const handleMouseLeave = () => {
    setHoverInfo(null);
    setMousePosition(null);
  };

  if (!labelVideoStore?.totalFrames) {
    return (
      <div style={{ marginTop: "15px", display: "flex", justifyContent: "center", alignItems: "center", height: "60px" }}>
        <CircularProgress size={28} style={{ color: "#aaa" }} />
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: `${INCIDENT_LIST_MARGIN_TOP}px`,
        display: "flex",
        flexDirection: "column",
        gap: `${INCIDENT_ROW_GAP}px`,
      }}
    >
      {classList.map((incidentClass) => {
        return (
          <div
            key={`class-${incidentClass}`}
            style={{ position: "relative", cursor: "pointer" }}
          >
            {/* INTERVALS ROW */}
            <div
              style={{
                position: "relative",
                width: "100%",
                height: `${rowHeight}px`,
                // border: "1px solid #e5e7eb",
                borderRadius: theme.borderRadius.small,
              }}
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const x = Math.max(
                  0,
                  Math.min(e.clientX - rect.left, rect.width)
                );
                const frameIndex = Math.floor(
                  (x / rect.width) * (labelVideoStore?.totalFrames || 0)
                );
                onFrameChange(frameIndex);
              }}
              // onMouseMove={handleMouseMove}
              // onMouseLeave={handleMouseLeave}
            >                          
              {labelVideoStore?.incidents
                .filter((incident) => incident.class?.name === incidentClass)
                .map((incident) => (
                  <IncidentIntervalItem
                    key={incident.id}
                    incident={incident}
                    clientWidth={clientWidth}
                    totalFrames={labelVideoStore?.totalFrames || 0}
                    currentFrame={labelVideoStore?.currentFrame ?? 0}
                    isEditing={
                      !readOnly &&
                      labelVideoStore?.clickedIncident === incident.id
                    }
                    isDragging={isDragging}
                    showLabel={!showLabels}
                    onSelect={(e) => {
                      e.stopPropagation();
                      if (!isDragging) {
                        if (readOnly) {
                          onFrameChange(incident.startFrame);
                        } else {
                          labelVideoStore?.setClickedIncident(incident.id);
                        }
                      }
                    }}
                    onSeekToStart={(e) => {
                      e.stopPropagation();
                      onFrameChange(incident.startFrame);
                    }}
                    onResizeMouseDown={(e, isLeft) =>
                      handleMouseDown(e, incident, isLeft)
                    }
                    onAdjustFrame={(isLeft, increment) =>
                      handleAdjustFrame(incident, isLeft, increment)
                    }
                  />
                ))}
              {labelVideoStore?.predictionIncidents
                .filter(
                  (prediction) => prediction.class?.name === incidentClass
                )
                .map((prediction) => (
                  <IncidentPredictionIntervalItem
                    key={prediction.id}
                    incident={prediction}
                    clientWidth={clientWidth}
                    totalFrames={labelVideoStore?.totalFrames || 0}
                    currentFrame={labelVideoStore?.currentFrame ?? 0}
                    showLabel={!showLabels}
                  />
                ))}
            </div>
            {hoverInfo && mousePosition && (
              <div
                style={{
                  position: "absolute",
                  left: mousePosition.x - 40,
                  top: -35,
                  backgroundColor: "rgba(0, 0, 0, 0.8)",
                  color: "white",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  fontSize: "12px",
                  zIndex: 1000,
                }}
              >
                Frame: {hoverInfo.frame} | Temps: {hoverInfo.time}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default inject("labelVideoStore")(observer(IncidentEditor));
